import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { DomainContactForm } from "../components/DomainContactForm";
import { Icon } from "../components/icons";
import { Alert, Button, Card, CardHeader, Field, Input, LoadingBlock, Mono, PageHeader, Select, Switch, cx } from "../components/ui";
import { QuotaAlert, quotaRefusal } from "../components/Quota";
import { ApiError, api } from "../lib/api";
import { formatMoney } from "../lib/money";
import {
  useApps,
  useBilling,
  useCheckDomains,
  useDomainContact,
  useDomainTlds,
  useRegisterDomain,
  useTransferDomain,
} from "../lib/queries";
import type { DomainAvailability, DomainContact, DomainTld } from "../lib/types";
import { splitDomainName } from "./domain/status";

/*
 * Three steps, named for the decision each asks for rather than the fields.
 *
 * A registration is three decisions — which name, who it is registered to,
 * and what to do with it — and a single page of inputs hides that: the
 * registrant is the one a first-time buyer does not know they need, and the
 * registrar refuses without it after the credit is taken. The wizard asks in
 * order, skips what is already answered, and ends with the name pointed at
 * an app, so nothing is left to do on the next page.
 */
const STEPS = ["name", "registrant", "options"] as const;
type Step = (typeof STEPS)[number];

type Mode = "register" | "transfer";

/** What step one produced: a name to register, or a name to bring in. */
type Choice =
  | { mode: "register"; result: DomainAvailability }
  | { mode: "transfer"; name: string; extension: string; authCode: string; tld: DomainTld };

export function NewDomainPage() {
  const { t } = useTranslation("domains");
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id ?? "";
  const [params] = useSearchParams();

  const contact = useDomainContact(orgId || undefined);
  const billing = useBilling(orgId || undefined);
  const currency = billing.data?.currency ?? "USD";
  const tlds = useDomainTlds(orgId || undefined);
  const saved = contact.data?.data ?? null;

  const [step, setStep] = useState<number>(0);
  const [choice, setChoice] = useState<Choice | null>(null);

  const choose = (next: Choice) => {
    setChoice(next);
    setStep(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t("new.title")} description={t("new.description")} />

      <Stepper step={step} onBack={setStep} />

      {contact.isPending ? (
        <Card>
          <LoadingBlock />
        </Card>
      ) : (
        <>
          {step === 0 && (
            <NameStep
              orgId={orgId}
              currency={currency}
              tlds={tlds.data?.data ?? []}
              initialMode={params.get("mode") === "transfer" ? "transfer" : "register"}
              initialQuery={params.get("q") ?? ""}
              onChoose={choose}
            />
          )}
          {step === 1 && choice && (
            <RegistrantStep orgId={orgId} orgName={currentOrg?.name ?? ""} contact={saved} choice={choice} onContinue={() => setStep(2)} />
          )}
          {step === 2 && choice && <OptionsStep orgId={orgId} currency={currency} choice={choice} />}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The stepper
// ---------------------------------------------------------------------------

function Stepper({ step, onBack }: { step: number; onBack: (index: number) => void }) {
  const { t } = useTranslation("domains");
  return (
    <ol className="flex items-stretch border border-line">
      {STEPS.map((key, index) => {
        const state = index === step ? "current" : index < step ? "done" : "todo";
        return (
          <li key={key} className="flex-1">
            <button
              type="button"
              // Backwards only. Jumping ahead would skip the decision that
              // makes the next step answerable.
              disabled={index > step}
              onClick={() => onBack(index)}
              className={cx(
                "flex w-full items-center gap-2 px-2.5 py-3 text-left text-xs transition-colors duration-200 ease-smooth sm:gap-2.5 sm:px-4 sm:text-sm",
                index > 0 && "border-l border-line",
                state === "current" && "bg-accent-soft/50 text-fg",
                state === "done" && "text-fg-muted hover:bg-raised",
                state === "todo" && "text-fg-subtle",
              )}
            >
              <span
                className={cx(
                  "grid size-5 shrink-0 place-items-center text-[11px] tabular-nums sm:size-6 sm:text-xs",
                  state === "current" && "bg-accent text-white",
                  state === "done" && "bg-mint text-canvas",
                  state === "todo" && "border border-line text-fg-subtle",
                )}
              >
                {state === "done" ? <Icon name="check" className="size-3.5" /> : index + 1}
              </span>
              <span className="truncate">{t(`new.steps.${key satisfies Step}`)}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — the name
// ---------------------------------------------------------------------------

/**
 * The order extensions are offered in when a bare name is typed. The shelf
 * is alphabetical and forty-odd long, and a search checks twenty names at
 * most: without this, "acme" would be checked against .agency and .art and
 * never against .net or .shop. The ones people actually mean come first;
 * everything else follows alphabetically and is reached by typing it.
 */
const PREFERRED = ["com", "net", "org", "io", "dev", "app", "ai", "co", "id", "shop", "store", "online", "site", "xyz", "me", "tech", "cloud", "info", "blog"];

export function orderExtensions(extensions: string[]): string[] {
  const rank = new Map(PREFERRED.map((ext, i) => [ext, i]));
  return [...extensions].sort((a, b) => {
    const ra = rank.get(a) ?? Number.MAX_SAFE_INTEGER;
    const rb = rank.get(b) ?? Number.MAX_SAFE_INTEGER;
    return ra !== rb ? ra - rb : a.localeCompare(b);
  });
}

/** How many extension chips show before "more". */
const CHIPS_SHOWN = 12;

/** What a query becomes: exact when it names an extension, one per extension on sale when not. */
export function expandQuery(query: string, extensions: string[]): string[] {
  const parts = query.split(/[\s,]+/).map((part) => part.trim().toLowerCase()).filter(Boolean);
  const names = parts.flatMap((part) => (part.includes(".") ? [part] : extensions.map((ext) => `${part}.${ext}`)));
  return [...new Set(names)].slice(0, 20);
}

function NameStep({
  orgId,
  currency,
  tlds,
  initialMode,
  initialQuery,
  onChoose,
}: {
  orgId: string;
  currency: string;
  tlds: DomainTld[];
  initialMode: Mode;
  initialQuery: string;
  onChoose: (choice: Choice) => void;
}) {
  const { t } = useTranslation("domains");
  const [mode, setMode] = useState<Mode>(initialMode);
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <div>
          <p className="text-sm font-medium text-fg">{t("new.name.title")}</p>
          <p className="mt-0.5 text-[13px] text-fg-muted">{t("new.name.description")}</p>
        </div>
        <div role="tablist" className="flex gap-1 rounded-md border border-line bg-raised p-1">
          {(["register", "transfer"] as const).map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={mode === item}
              onClick={() => setMode(item)}
              className={cx(
                "rounded px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-200 ease-smooth",
                mode === item ? "bg-surface text-fg shadow-xs" : "text-fg-muted hover:text-fg",
              )}
            >
              {t(`new.tabs.${item}`)}
            </button>
          ))}
        </div>
      </div>
      {tlds.length === 0 ? (
        /* Nothing on the shelf: say so once, here, rather than answering
           "not sold here" beside every name typed. */
        <div className="px-5 py-5">
          <Alert tone="info">{t("new.search.nothingOnSale")}</Alert>
        </div>
      ) : mode === "register" ? (
        <Search orgId={orgId} currency={currency} tlds={tlds} initialQuery={initialQuery} onChoose={(result) => onChoose({ mode: "register", result })} />
      ) : (
        <TransferForm tlds={tlds} currency={currency} onChoose={(name, extension, authCode, tld) => onChoose({ mode: "transfer", name, extension, authCode, tld })} />
      )}
    </Card>
  );
}

function Search({
  orgId,
  currency,
  tlds,
  initialQuery,
  onChoose,
}: {
  orgId: string;
  currency: string;
  tlds: DomainTld[];
  initialQuery: string;
  onChoose: (result: DomainAvailability) => void;
}) {
  const { t } = useTranslation("domains");
  const [query, setQuery] = useState(initialQuery);
  const check = useCheckDomains(orgId);
  const extensions = useMemo(() => orderExtensions(tlds.map((tld) => tld.extension)), [tlds]);
  const [allChips, setAllChips] = useState(false);
  const chips = allChips ? extensions : extensions.slice(0, CHIPS_SHOWN);

  const search = (event: FormEvent) => {
    event.preventDefault();
    const names = expandQuery(query, extensions);
    if (names.length > 0) check.mutate(names);
  };

  /*
   * A query that arrived in the URL -- the marketing site's search box hands
   * over with `?q=` -- is run once the shelf is known, so the person lands
   * on results rather than on a box holding what they already typed.
   */
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current || !initialQuery || extensions.length === 0) return;
    ran.current = true;
    const names = expandQuery(initialQuery, extensions);
    if (names.length > 0) check.mutate(names);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery, extensions]);

  const results = check.data?.data ?? [];
  const available = results.filter((r) => r.available).length;

  return (
    <>
      <form onSubmit={search} className="px-5 pt-5 pb-5">
        {/*
          The box is the step. Large, mono, and the only thing asking for
          input — a registrar's front door is a search field.
        */}
        <div className="flex items-stretch gap-2">
          <div className="relative min-w-0 flex-1">
            <Icon name="search" className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-fg-subtle" />
            <input
              id="domain-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("new.search.placeholder")}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              aria-label={t("new.search.label")}
              className="h-12 w-full rounded-md border border-line bg-raised pr-4 pl-11 font-mono text-[17px] text-fg placeholder:text-fg-subtle/70 focus:border-accent focus:ring-2 focus:ring-accent/40 focus:outline-none"
            />
          </div>
          <Button type="submit" loading={check.isPending} className="h-12 px-5">
            {t("new.search.button")}
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <p className="text-[12px] text-fg-subtle">{t("new.search.hint")}</p>
          {extensions.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {chips.map((ext) => (
                <li key={ext}>
                  <button
                    type="button"
                    onClick={() => setQuery((q) => (q.includes(".") ? `${q.split(".")[0]}.${ext}` : q ? `${q}.${ext}` : `.${ext}`))}
                    className="rounded border border-line px-2 py-0.5 font-mono text-[11px] text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong hover:text-fg"
                  >
                    .{ext}
                  </button>
                </li>
              ))}
              {extensions.length > CHIPS_SHOWN && (
                <li>
                  <button
                    type="button"
                    onClick={() => setAllChips((open) => !open)}
                    className="rounded px-2 py-0.5 text-[11px] text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
                  >
                    {allChips ? t("new.search.fewer") : t("new.search.more", { count: extensions.length - CHIPS_SHOWN })}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      </form>

      {check.error && (
        <div className="border-t border-line px-5 py-4">
          <Alert>{check.error.message}</Alert>
        </div>
      )}

      {results.length > 0 && (
        <>
          <div className="flex items-baseline justify-between border-t border-line px-5 py-3">
            <p className="micro text-fg-subtle">{t("new.result.count", { count: results.length })}</p>
            <p className="text-[12px] text-fg-subtle">{t("new.result.available_count", { count: available })}</p>
          </div>
          <ul className="border-t border-line">
            {results.map((result, i) => (
              <li key={result.domain} style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }} className="animate-rise-in border-b border-line last:border-b-0">
                <ResultRow result={result} currency={currency} onChoose={() => onChoose(result)} />
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function ResultRow({ result, currency, onChoose }: { result: DomainAvailability; currency: string; onChoose: () => void }) {
  const { t } = useTranslation("domains");
  const buyable = result.available && result.sellMicros !== null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3.5">
      <span aria-hidden className={cx("size-2 shrink-0 rounded-full", buyable ? "bg-mint" : result.premium ? "bg-warn" : "bg-line-strong")} />
      <div className="min-w-0 flex-1">
        <p className={cx("font-mono text-[15px]", buyable ? "text-fg" : "text-fg-muted line-through decoration-line-strong")}>{result.domain}</p>
        <p className={cx("text-[12px]", buyable ? "text-mint" : "text-fg-subtle")}>
          {buyable ? t("new.result.available") : (result.reason ?? t("new.result.unavailable"))}
        </p>
      </div>
      {buyable && (
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm tabular-nums text-fg">{t("new.result.perYear", { price: formatMoney(result.sellMicros as number, currency) })}</p>
            {result.renewMicros !== null && result.renewMicros !== result.sellMicros && (
              <p className="text-[11px] text-fg-subtle">{t("new.result.renewsAt", { price: formatMoney(result.renewMicros, currency) })}</p>
            )}
          </div>
          <Button size="sm" onClick={onChoose}>
            {t("new.result.choose")}
            <Icon name="chevron" className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function TransferForm({
  tlds,
  currency,
  onChoose,
}: {
  tlds: DomainTld[];
  currency: string;
  onChoose: (name: string, extension: string, authCode: string, tld: DomainTld) => void;
}) {
  const { t } = useTranslation("domains");
  const [domain, setDomain] = useState("");
  const [authCode, setAuthCode] = useState("");
  const split = splitDomainName(domain);
  const tld = split ? tlds.find((row) => row.extension === split.extension) : undefined;
  const price = tld?.transferMicros ?? null;
  const ready = Boolean(split && tld && price !== null && authCode.trim());

  return (
    <form
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (split && tld && ready) onChoose(split.name, split.extension, authCode.trim(), tld);
      }}
      className="space-y-5 px-5 py-5"
    >
      <Field label={t("new.transfer.domain")} htmlFor="transfer-domain">
        <Input id="transfer-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder={t("new.search.placeholder")} className="font-mono" autoComplete="off" spellCheck={false} />
      </Field>
      <Field label={t("new.transfer.authCode")} htmlFor="transfer-code" hint={t("new.transfer.authCodeHint")}>
        <Input id="transfer-code" value={authCode} onChange={(e) => setAuthCode(e.target.value)} className="font-mono" autoComplete="off" spellCheck={false} />
      </Field>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12px] text-fg-subtle">
          {split && price === null
            ? t("new.transfer.notSold", { extension: split.extension })
            : price !== null
              ? t("new.transfer.price", { price: formatMoney(price, currency) })
              : t("new.transfer.note")}
        </p>
        <Button type="submit" disabled={!ready}>
          {t("new.result.choose")}
          <Icon name="chevron" className="size-3.5" />
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — the registrant
// ---------------------------------------------------------------------------

function RegistrantStep({
  orgId,
  orgName,
  contact,
  choice,
  onContinue,
}: {
  orgId: string;
  orgName: string;
  contact: DomainContact | null;
  choice: Choice;
  onContinue: () => void;
}) {
  const { t } = useTranslation("domains");
  const [editing, setEditing] = useState(contact === null);
  const name = choice.mode === "register" ? choice.result.domain : `${choice.name}.${choice.extension}`;

  return (
    <Card>
      <CardHeader
        title={t("new.contact.title")}
        description={t("new.contact.forName", { domain: name, organization: orgName })}
        action={
          contact && !editing ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              {t("new.contact.edit")}
            </Button>
          ) : undefined
        }
      />
      <div className="px-5 py-5">
        {editing ? (
          <DomainContactForm
            orgId={orgId}
            contact={contact}
            onSaved={() => {
              setEditing(false);
              onContinue();
            }}
            onCancel={contact ? () => setEditing(false) : undefined}
          />
        ) : contact ? (
          <div className="space-y-5">
            <RegistrantSummary contact={contact} />
            <Button onClick={onContinue}>
              {t("new.contact.use")}
              <Icon name="chevron" className="size-3.5" />
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function RegistrantSummary({ contact }: { contact: DomainContact }) {
  const { t } = useTranslation("domains");
  const initials = `${contact.firstName[0] ?? ""}${contact.lastName[0] ?? ""}`.toUpperCase();
  return (
    <div className="flex flex-wrap items-start gap-4">
      <span aria-hidden className="grid size-9 shrink-0 place-items-center rounded-md border border-line bg-raised font-mono text-[12px] tracking-[0.08em] text-fg-muted">
        {initials}
      </span>
      <div className="min-w-0 flex-1 text-sm leading-6 text-fg-muted">
        <p className="text-fg">
          {contact.firstName} {contact.lastName}
          {contact.companyName && <span className="text-fg-muted"> · {contact.companyName}</span>}
        </p>
        <p>
          {contact.street} {contact.houseNumber}, {contact.city}
          {contact.state && <>, {contact.state}</>} {contact.zipcode}, {contact.country}
        </p>
        <p className="flex flex-wrap items-center gap-x-3">
          <span>{contact.email}</span>
          <span>{contact.phoneCountryCode} {contact.phoneNumber}</span>
          <span className="flex items-center gap-1.5 text-[12px] text-fg-subtle">
            <span className={cx("size-1.5 rounded-full", contact.emailVerified ? "bg-mint" : "bg-warn")} />
            {contact.emailVerified ? t("new.contact.verified") : t("new.contact.unverified")}
          </span>
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — options, and the purchase
// ---------------------------------------------------------------------------

/** Periods worth offering: the common ones inside the extension's range. */
export function periodsFor(min: number, max: number): number[] {
  const common = [1, 2, 3, 5, 10].filter((n) => n >= min && n <= max);
  return common.length > 0 ? common : [Math.max(1, min)];
}

function OptionsStep({ orgId, currency, choice }: { orgId: string; currency: string; choice: Choice }) {
  const { t } = useTranslation("domains");
  const navigate = useNavigate();
  const tlds = useDomainTlds(orgId);
  const apps = useApps(orgId, 100);

  const extension = choice.mode === "register" ? choice.result.extension : choice.extension;
  const domainName = choice.mode === "register" ? choice.result.domain : `${choice.name}.${choice.extension}`;
  const tld = choice.mode === "transfer" ? choice.tld : tlds.data?.data.find((row) => row.extension === extension);
  const privacyAllowed = tld?.privateWhoisAllowed ?? true;
  const periods = choice.mode === "register" ? periodsFor(choice.result.minPeriod, choice.result.maxPeriod) : [1];
  const perYear = choice.mode === "register" ? (choice.result.sellMicros ?? 0) : (choice.tld.transferMicros ?? 0);

  const [years, setYears] = useState(periods[0] ?? 1);
  const [privacy, setPrivacy] = useState(true);
  const [autorenew, setAutorenew] = useState(true);
  const [appId, setAppId] = useState("");
  const [phase, setPhase] = useState<"idle" | "registering" | "pointing">("idle");

  const register = useRegisterDomain(orgId);
  const transfer = useTransferDomain(orgId);
  const error = register.error ?? transfer.error;
  // Every credit shortfall — never paid, promotional only, or plain short —
  // is the same next step: add credit. The API's sentence says which.
  const needsCredit = error instanceof ApiError && error.code === "insufficient_credit";
  const total = perYear * years;
  const chosenApp = apps.data?.data.find((app) => app.id === appId);

  /*
   * Register, then point, then leave. Pointing can only happen on an active
   * domain, so a transfer — pending until the losing registrar lets go —
   * skips it; the overview offers it once the name arrives.
   */
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setPhase("registering");
    try {
      const domain =
        choice.mode === "register"
          ? await register.mutateAsync({ name: choice.result.name, extension, period: years, privateWhois: privacy && privacyAllowed, autorenew })
          : await transfer.mutateAsync({ name: choice.name, extension, authCode: choice.authCode, privateWhois: privacy && privacyAllowed, autorenew });
      let notice = t(domain.status === "active" ? "new.buy.registered" : "new.buy.pending", { domain: domain.domain });
      // Non-fatal, from the API: the name is bought and something beside it
      // was not — WHOIS privacy, for want of a contract on our side.
      const warning = domain.warning ? ` ${t("new.buy.noPrivacy")}` : "";
      if (appId && domain.status === "active") {
        setPhase("pointing");
        try {
          // A bare call rather than `usePointDomain`: the hook binds to an id
          // at construction, and the id exists only after the purchase. The
          // page it lands on fetches the domain fresh, so nothing is stale.
          await api(`/v1/orgs/${orgId}/domains/${domain.id}/point`, { method: "POST", body: { appId } });
          notice = t("new.buy.registeredAndPointed", { domain: domain.domain, app: chosenApp?.name ?? "" });
        } catch {
          // The name is bought; pointing can be done from its page. Say so
          // rather than reporting the purchase as failed.
          notice = t("new.buy.registeredNotPointed", { domain: domain.domain });
        }
      }
      navigate(`/domains/${domain.id}`, { state: { notice: notice + warning } });
    } catch {
      setPhase("idle");
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-px overflow-hidden rounded-lg border border-line bg-line lg:grid-cols-[1fr_20rem]">
      <div className="space-y-6 bg-surface px-5 py-5">
        {error && !needsCredit && (quotaRefusal(error) ? <QuotaAlert refusal={quotaRefusal(error)!} /> : <Alert>{error.message}</Alert>)}
        {needsCredit && (
          <Alert tone="warning">
            <span>
              {error.message}{" "}
              <Link to="/settings/billing" className="underline">
                {t("new.addCredit")}
              </Link>
            </span>
          </Alert>
        )}

        {choice.mode === "register" && (
          <div>
            <p className="micro text-fg-subtle">{t("new.buy.years")}</p>
            <div role="radiogroup" className="mt-2 flex flex-wrap gap-1.5">
              {periods.map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={years === n}
                  onClick={() => setYears(n)}
                  className={cx(
                    "min-w-16 rounded-md border px-3 py-2 text-[13px] tabular-nums transition-colors duration-200 ease-smooth",
                    years === n ? "border-inverse bg-inverse text-on-inverse" : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
                  )}
                >
                  {t("new.buy.year", { count: n })}
                </button>
              ))}
            </div>
          </div>
        )}

        <ToggleRow
          label={t("new.buy.privacy")}
          hint={privacyAllowed ? t("new.buy.privacyHint") : t("new.buy.privacyUnavailable")}
          checked={privacy && privacyAllowed}
          onChange={setPrivacy}
          disabled={!privacyAllowed}
        />
        <ToggleRow label={t("new.buy.autorenew")} hint={t("new.buy.autorenewHint")} checked={autorenew} onChange={setAutorenew} />

        {choice.mode === "register" ? (
          <div>
            <p className="text-[13px] font-medium text-fg">{t("new.buy.pointTitle")}</p>
            <p className="mt-0.5 mb-2 max-w-md text-[12px] leading-5 text-fg-subtle">{t("new.buy.pointHint")}</p>
            {/* The width goes on a wrapper: Select draws its chevron against
                its own wrapper's edge, so a narrow select in a wide one puts
                the arrow at the far side of the card. */}
            <div className="sm:w-72">
              <Select value={appId} onChange={(e) => setAppId(e.target.value)} aria-label={t("new.buy.pointTitle")}>
                <option value="">{t("new.buy.pointLater")}</option>
                {(apps.data?.data ?? []).map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        ) : (
          <p className="text-[12px] leading-5 text-fg-subtle">{t("new.transfer.note")}</p>
        )}
      </div>

      {/* The receipt: what is about to leave the balance, and the one sentence
          about it being final, beside the button that makes it so. */}
      <aside className="flex flex-col justify-between bg-surface px-5 py-5">
        <dl className="space-y-2 text-[13px]">
          <div className="flex justify-between gap-3">
            <dt>
              <Mono className="text-fg">{domainName}</Mono>
            </dt>
            <dd className="tabular-nums text-fg-muted">
              {choice.mode === "register" ? `${t("new.buy.year", { count: years })} × ${formatMoney(perYear, currency)}` : t("new.tabs.transfer")}
            </dd>
          </div>
          {chosenApp && (
            <div className="flex justify-between gap-3 text-fg-muted">
              <dt>{t("new.buy.pointsAt")}</dt>
              <dd>{chosenApp.name}</dd>
            </div>
          )}
          <div className="flex items-baseline justify-between gap-3 border-t border-line pt-3">
            <dt className="micro text-fg-subtle">{t("new.buy.total")}</dt>
            <dd className="text-2xl tabular-nums text-fg">{formatMoney(total, currency)}</dd>
          </div>
        </dl>
        <div className="mt-5 space-y-3">
          <Button type="submit" loading={phase !== "idle"} className="w-full">
            {phase === "pointing"
              ? t("new.buy.pointing")
              : choice.mode === "register"
                ? t("new.buy.confirm", { price: formatMoney(total, currency) })
                : t("new.transfer.confirm", { price: formatMoney(total, currency) })}
          </Button>
          <p className="text-[11px] leading-5 text-fg-subtle">{t("new.buy.terms")}</p>
        </div>
      </aside>
    </form>
  );
}

export function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-[13px] font-medium text-fg">{label}</p>
        <p className="mt-0.5 max-w-md text-[12px] leading-5 text-fg-subtle">{hint}</p>
      </div>
      <Switch checked={checked} onChange={onChange} disabled={disabled} label={label} />
    </div>
  );
}
